import Popover from "@material-ui/core/Popover";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { useNotification } from "../../context/notification";
import { routes } from "../../routes.js";
import { apiRequest } from "../../utils/api";
import { getUID } from "../../utils/auth";
import styles from "./index.module.scss";

dayjs.extend(relativeTime);
let socket;

const getHumanFormattedDate = dateString => {
  // if it's a valid date, do date formatting.
  let inputDate = new Date(dateString);
  let currentDate = new Date();
  let diffDays = "";

  if (inputDate > currentDate) {
    // if activity Date is yet to happen
    diffDays = dayjs(inputDate).toNow();
  } else {
    // if activity is already in the past
    diffDays = dayjs(inputDate).fromNow();
  }

  let timeOfDay = dayjs(inputDate).format("h:mm A");

  return `${diffDays}, ${timeOfDay}`;
};

export default function NotificationPopover({
  open,
  anchorEl,
  onClose,
  ...props
}) {
  const { addNotification } = useNotification();
  const { t } = useTranslation();
  const [listHeight, setListHeight] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [list, setList] = useState([]);
  const [urlMap, setUrlMap] = useState({});
  const history = useHistory();

  useEffect(() => {
    setUrlMap({
      session_grow_form_notification: {
        coach: routes.sessionsDetails.path,
        coachee: routes.sessionsDetails.path,
      },
      session_create: {
        coach: routes.sessionsDetails.path,
        coachee: routes.sessionsDetails.path,
      },
      session_notification: {
        coach: routes.sessionsDetails.path,
        coachee: routes.sessionsDetails.path,
      },
      session_checkpoint_progression_notification: {
        coach: routes.sessionsSetCheckpoints.path,
        coachee: routes.sessionsCoachingSummary.path,
      },
      session_checkpoint_notification: {
        coach: routes.sessionsUpdateCheckpoints.path,
        coachee: routes.sessionsCoachingSummary.path,
      },
      review_date_postpone_notification: {
        coach: "/",
        coachee: routes.sessionsCoachingSummary.path,
      },
      review_today: {
        coach: routes.sessionsReviewSession.path,
        coachee: routes.sessionsCoachingSummary.path,
      },
      compliment: {
        coach: routes.sessionsReviewSession.path,
        coachee: "/",
      },
      rating_pending_pdca: {
        coach: routes.sessionsReviewSummary.path,
        coachee: routes.sessionsArchiveSession.path,
      },
      rating_archived_pdca: {
        coach: routes.sessionsReviewSummary.path,
        coachee: routes.sessionsArchiveSession.path,
      },
      session_monthly_total: {
        coach: routes.logs.path,
        coachee: routes.logs.path,
      },
    });
  }, []);

  const updateNotifications = data => {
    setIsLoading(false);
    if (!_.isEqual(data, list)) {
      setList(data);
    }
  };
  useEffect(() => {
    if (open) {
      //  setInterval(() => {
      getNotification();
      //}, 10000);
    }
  }, [open]);

  const getNotification = async () => {
    const response = await apiRequest.get(
      `/api/notification/session?uid=${getUID()}`
    );
    if (response.status === 200) {
      updateNotifications(response.data.data);
      setIsLoading(false);
    } else {
      setIsLoading(false);
      addNotification(
        t("error.couldNotGetNotification") +
        ` (Status code: ${response.status})`,
        "error"
      );
    }
  };

  // manually calculate and set notification panel's
  // outer height accordingly to fit available content
  useEffect(() => {
    if (list && list.length <= 0) {
      setListHeight(100);
    } else {
      setTimeout(() => {
        let listElem = document.getElementById("notifications-list");
        if (listElem) {
          let height = 0;
          listElem.childNodes.forEach(o => {
            height += o.offsetHeight;
          });
          setListHeight(height);
        }
      }, 10);
    }
  }, [open, list, isLoading]);

  const getTranslated = string => {
    let currentString = string;
    const regex = /\bt\("([A-Za-z0-9-:.]*)\b"\)/g;
    let translationCode = currentString.match(regex);
    if (translationCode && translationCode.length > 0) {
      translationCode.forEach(str => {
        currentString = currentString.replace(str, eval(str));
      });
    }

    return currentString;
  };

  return (
    <Popover
      className={styles.popover}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      elevation={4}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      {...props}
    >
      {list && (
        <div
          style={{ height: `${list.length > 0 ? listHeight + 56 : 140}px` }}
          className={styles.contentWrapper}
        >
          <div className={styles.header} style={{ pointerEvents: "none" }}>
            {t("header.notifications")}
          </div>

          <div className={styles.list} id="notifications-list">
            {!!!error ? (
              !!getUID() ? (
                isLoading ? (
                  <div className={styles.empty}>
                    {t("header.loadingNotifications")}
                  </div>
                ) : list.length > 0 ? (
                  // has no error, has UID, is not loading, have notifications
                  list.map((item, index) => {
                    let parsedMsg = item.message;

                    let spanRegex = /<span>([A-Z0-9-: ]*)<\/span>/;
                    // /\bt\("([A-Za-z0-9-:.]*)\b"\)/
                    let spanRegexGlobal = /<span>[A-Z0-9-: ]*<\/span>/g;

                    // attempt to capture <span> tag group
                    let spanMatch = item.message.match(spanRegexGlobal);
                    if (spanMatch && spanMatch.length > 0) {
                      spanMatch.forEach(unformattedDate => {
                        //get inner date string within the <span> tags
                        let outerText = unformattedDate.match(spanRegex)[0];
                        let innerText = unformattedDate.match(spanRegex)[1];

                        // checking if the contents within the spanGroup is a valid date
                        let parsedDate = Date.parse(innerText);
                        if (typeof parsedDate === "number" && parsedDate > 0) {
                          // if so, format it into human readable date
                          let humanDate = getHumanFormattedDate(parsedDate);

                          // replace in parsedMsg
                          parsedMsg = parsedMsg.replace(outerText, humanDate);
                        }
                      });
                    }

                    // search and replace curly bracket wrappers
                    let curlyBracketsRegexGlobal = /\{\{([^\{\{^\}\}]*)\}\}/g;
                    let curlyBracketsRegex = /\{\{([^\{\{^\}\}]*)\}\}/;
                    let curlyMatch = parsedMsg.match(curlyBracketsRegexGlobal);
                    if (curlyMatch && curlyMatch.length > 0) {
                      curlyMatch.forEach(curlyString => {
                        let outerText =
                          curlyString.match(curlyBracketsRegex)[0];
                        let innerText =
                          curlyString.match(curlyBracketsRegex)[1];
                        // checking if the contents within the curly brackets is a valid date
                        let parsedDate = Date.parse(innerText);
                        if (typeof parsedDate === "number" && parsedDate > 0) {
                          let humanDate = getHumanFormattedDate(parsedDate);
                          // replace in parsedMsg
                          parsedMsg = parsedMsg.replace(outerText, humanDate);
                        } // else, do nothing
                      });
                    }

                    // remove leftover curly brackets
                    parsedMsg = parsedMsg.replace("{{", "");
                    parsedMsg = parsedMsg.replace("}}", "");

                    return (
                      <div
                        key={index}
                        className={styles.listItem}
                        onClick={() => {
                          let urlMapObj = urlMap[item.type];
                          if (!urlMapObj) {
                            console.error("no valid urlMap found!");
                          } else {
                            if (!item.for_user) {
                              console.error(
                                "this notification's `for_user` field is empty!",
                                item
                              );
                            } else {
                              let path = urlMapObj[item.for_user];
                              path = path.replace(/:id/, item.session_id);
                              history.push(path);
                            }
                          }
                        }}
                      >
                        <div
                          className={styles.text}
                          dangerouslySetInnerHTML={{
                            __html: getTranslated(parsedMsg),
                          }}
                        ></div>
                        <div className={styles.timestamp}>
                          {/* Time format: 8.00am, 9 January 2020 */}
                          {dayjs(item.send_at).format("h.mma, D MMMM YYYY")}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  // notifications list is empty
                  <div className={styles.empty}>You have no notifications</div>
                )
              ) : (
                // no UID in localstorage
                <div className={styles.empty}>
                  Could not fetch notifications, please log-in again.
                </div>
              )
            ) : (
              // has error
              <div className={styles.empty}>{error}</div>
            )}
          </div>
        </div>
      )}
    </Popover>
  );
}
